import { Checkbox as CarbonCheckbox } from "@carbon/react";

import {
  InfoIcon,
  ModalContainer,
  SectionHeader,
  SectionSubheader,
  Select,
  SwitchAppButton,
  SwitchAppIconButton,
} from "../../../components";
import { currencies, MAX_ITEM_QTY } from "../../../constants";
import {
  HookFormCheckbox,
  HookFormSelect,
  HookFormToggle,
  SubmitButton,
  TextInput,
} from "../../../hook-form";
import { ICreateEditPaymentLinkFormViewProps } from "./CreateEditPaymentLinkForm";

import closeIconDark from "../../../assets/icons/close-circle-red.svg";
import { PaymentLinkPreview } from "../PaymentLinkPreview";
import { SelectedProductDisplay } from "./SelectedProductDisplay";
import { formatAsCurrency } from "../../../utils";
import { PayoutBankAccountDisplay } from "./PayoutBankAccountDisplay";

export const View = ({
  isEditMode,

  isModalOpen,
  closeModal,

  errors,
  handleSubmit,
  register,

  // payout tags
  isLoadingBankAccounts,
  bankAccountsInTagList,
  bankAccountsNotInTagList,
  payoutTagsFields,
  appendPayoutTagField,
  removePayoutTagField,
  onToggleSetSplitType,

  // values used for settings form
  lineItemsFields,
  appendNewPaymentLinkLineItemsField,
  removePaymentLinkLineItemsField,
  resetLineItemsToDefault,
  formValues,
  isLoadingProducts,
  products,
}: ICreateEditPaymentLinkFormViewProps) => {
  const productsNotSelected = products.filter(
    (prod) => !formValues.line_items.find((p) => p.product === prod.id)
  );

  const productsInPaymentLink = products.filter((prod) =>
    formValues.line_items.find((p) => p.product === prod.id)
  );

  const invalidPaymentLinkItemExists =
    formValues.line_items.find((item) => !item.product) !== undefined;

  const bankAccountsNotInTagListSelectOptions = bankAccountsNotInTagList.map(
    (acct) => ({
      groupLabel: `${acct.account_name} - ${acct.account_number} (${acct.bank_name})`,
      options: acct.bank_account_tags.map((tag) => ({
        text: `${tag.tag_name}`,
        value: tag.id,
      })),
    })
  );

  return (
    <form onSubmit={handleSubmit}>
      <ModalContainer
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        modalHeader={
          <SectionHeader
            title={`${isEditMode ? "Edit" : "Create"} Payment Link`}
            subtitle="Customers can use payment links to buy directly from you."
            renderRight={
              <SubmitButton
                size="sm"
                className="ml-5 mr-5 pl-5 pr-5"
                showChildrenWithLoadingIcon
              >
                {isEditMode ? "Save Changes" : "Create link"}
              </SubmitButton>
            }
          />
        }
        fullscreen
      >
        <div className="payment-link-form--container flex-container flex-break--small">
          <div className="flex-box">
            {/* <HookFormSelect
              name="is_donation_payment_link"
              label="Payment Link Type"
              selectItems={[
                {
                  text: "Products and subscriptions",
                  value: "false",
                },
                {
                  text: "Customer chooses what to pay",
                  value: "true",
                },
              ]}
              register={register}
              errors={errors}
            /> */}
            <TextInput
              name="payment_link_name"
              label="Payment Link Name (optional)"
              infoIconText={`A name to display on your dashboard to identify this payment link.`}
              type="text"
              size="sm"
              // className="mb-3"
              register={register}
              errors={errors}
            />

            {formValues.id ? null : (
              <TextInput
                name="friendly_url"
                label="Friendly Url (optional)"
                infoIconText={`A friendly url to display to customers for this payment link. eg https://pay.switchappgo.com/CUSTOM_URL`}
                type="text"
                size="sm"
                className="friendlyUrlInput"
                register={register}
                errors={errors}
              />
            )}

            <HookFormSelect
              name="currency"
              label="Currency"
              infoIconText="The currency type for all products in this payment link."
              selectItems={currencies}
              // selectGroupedItems={}
              register={register}
              errors={errors}
            />

            <div className="line-items-list--container">
              {!formValues.currency
                ? null
                : lineItemsFields.map((field, index) => {
                    const selectedProduct = products.find(
                      (p) => p.id === formValues.line_items[index].product
                    );

                    return (
                      <div
                        key={field.id} // important to include key with field's id
                        className="line-item--container"
                      >
                        {
                          // field.product !== "" ? (
                          <div className="close-button--container">
                            <SwitchAppIconButton
                              size="sm"
                              kind="tertiary"
                              className="line-item--remove-button"
                              onClick={() =>
                                lineItemsFields.length > 1
                                  ? removePaymentLinkLineItemsField(index)
                                  : resetLineItemsToDefault(index)
                              }
                            >
                              <img
                                src={closeIconDark}
                                alt="close icon"
                                className="close-icon"
                              />
                            </SwitchAppIconButton>
                          </div>
                          // ) : null
                        }

                        {formValues.line_items[index].product ? (
                          !selectedProduct ? (
                            <></>
                          ) : (
                            <>
                              <div className="flex-box flex-1_2">
                                <SelectedProductDisplay
                                  product={selectedProduct}
                                />
                              </div>

                              <div className="">
                                <HookFormCheckbox
                                  name={`line_items.${index}.is_qty_adjustable_by_customer`}
                                  label="Allow customer to change quantity"
                                  infoIconText="Indicates if a customer can change the quantity they want to purchase at checkout"
                                  register={register}
                                  errors={errors}
                                />
                              </div>

                              <div className="flex-container">
                                <div className="flex-box">
                                  <TextInput
                                    name={`line_items.${index}.qty`}
                                    label="Qty"
                                    infoIconText="Quantity customer will be purchasing using this link"
                                    type="number"
                                    size="sm"
                                    className="switchapp-small--input"
                                    // arrayFieldName="line_items"
                                    // arrayFieldIndex={index}
                                    register={register}
                                    errors={errors}
                                  />
                                </div>
                                <div className="flex-box">
                                  {!formValues.line_items[index]
                                    .is_qty_adjustable_by_customer ? null : (
                                    <TextInput
                                      name={`line_items.${index}.min_qty`}
                                      label="Min."
                                      infoIconText="Minimum quantity customer will be able to purchase"
                                      type="number"
                                      size="sm"
                                      className="switchapp-small--input"
                                      register={register}
                                      errors={errors}
                                    />
                                  )}
                                </div>
                                <div className="flex-box">
                                  {!formValues.line_items[index]
                                    .is_qty_adjustable_by_customer ? null : (
                                    <TextInput
                                      name={`line_items.${index}.max_qty`}
                                      label="Max."
                                      infoIconText={`Maximum quantity customer can purchase. This value must be less than ${MAX_ITEM_QTY}.`}
                                      type="number"
                                      size="sm"
                                      className="switchapp-small--input"
                                      register={register}
                                      errors={errors}
                                    />
                                  )}
                                </div>
                              </div>
                            </>
                          )
                        ) : (
                          <HookFormSelect
                            name={`line_items.${index}.product`}
                            label="Product"
                            infoIconText="Select a product to add to the payment link."
                            showSkeleton={isLoadingProducts}
                            selectItems={[]}
                            // selectItems={
                            //   productsNotSelected.map((prod) => ({
                            //     text: `${prod.currency} ${prod.price}`,
                            //     value: prod.id,
                            //   })) || []
                            // }
                            selectGroupedItems={
                              productsNotSelected.map((prod) => ({
                                groupLabel: prod.product_name,
                                options: [
                                  {
                                    text: `${prod.currency} ${formatAsCurrency(
                                      prod.price
                                    )}`,
                                    value: prod.id,
                                  },
                                ],
                              })) || []
                            }
                            register={register}
                            errors={errors}
                          />
                        )}
                      </div>
                    );
                  })}
            </div>

            {formValues.line_items.length === 0 ? null : (
              <div className="flex-container justify-content--end">
                <SwitchAppButton
                  onClick={appendNewPaymentLinkLineItemsField}
                  kind="success"
                  className="mt-5"
                  size="sm"
                  disabled={
                    productsNotSelected.length === 0 ||
                    invalidPaymentLinkItemExists
                  }
                >
                  + Add another product
                </SwitchAppButton>
              </div>
            )}

            <SectionSubheader
              title="Customer Details"
              infoIconDescription="advanced options for the payment link that determine
               how your customer requirements at checkout"
              className="customer-options--subheader"
            />

            <div className="flex-container flex-break--small options--container">
              <div className="flex-box">
                <HookFormCheckbox
                  name="require_customer_full_name"
                  label="Require customer name"
                  containerClassName="no-top-margin"
                  infoIconText="Ensures customer enters their full name at checkout"
                  register={register}
                  errors={errors}
                />

                <HookFormCheckbox
                  name="require_customer_address"
                  label="Require customer delivery address"
                  containerClassName="no-top-margin"
                  infoIconText="Ensures customer enters a delivery address at checkout"
                  register={register}
                  errors={errors}
                />

                <HookFormCheckbox
                  name="require_customer_phone_number"
                  label="Require customer phone number"
                  containerClassName="no-top-margin"
                  infoIconText="Ensures customer enters a phone number at checkout"
                  register={register}
                  errors={errors}
                />

                {/* <Checkbox
                  name="allow_promo_codes"
                  label="Allow Promo Codes"
                  containerClassName="no-top-margin"
                  infoIconText="Enable application of promo codes to this payment link."
                  register={register}
                  errors={errors}
                /> */}
              </div>

              {/* <div className="flex-box">
                <Checkbox
                  name="collect_tax_automatically"
                  label="Collect tax automatically"
                  containerClassName="no-top-margin"
                  infoIconText="Automatically charge item taxes on checkout"
                  register={register}
                  errors={errors}
                />

                <Checkbox
                  name="allow_business_customers_to_provide_tax_id"
                  label="Allow businesses to provide tax id"
                  containerClassName="no-top-margin"
                  infoIconText="This displays an additional field for businesses to provide their VAT or other tax ID. We only display this field to customers in certain countries."
                  register={register}
                  errors={errors}
                />

                <Checkbox
                  name="save_payment_for_future_use"
                  label="Save payment for future use"
                  containerClassName="no-top-margin"
                  infoIconText="Select this option if you plan to charge a deposit and then collect the full amount later."
                  register={register}
                  errors={errors}
                />
              </div> */}
            </div>

            {/* Payment Link Payout tags */}
            <SectionSubheader
              title="Payout Settings"
              infoIconDescription="Configure how purchases with this link will be split to your bank accounts"
            />
            <HookFormCheckbox
              name="enable_instant_settlement_providers_only"
              label="Enable Instant Settlement"
              containerClassName="no-top-margin"
              infoIconText="Configures checkout to display only payment options that will settle the received funds immediately."
              register={register}
              errors={errors}
            />

            {!formValues.transaction_payout_tags.length ? null : (
              <div className="flex-container">
                <div>
                  <CarbonCheckbox
                    name={`uses_payout_tag_percentage`}
                    id={`uses_payout_tag_percentage`}
                    labelText="Use percentages for splitting"
                    checked={formValues.uses_payout_tag_percentage}
                    onChange={onToggleSetSplitType}
                    // onToggle={onToggleSetSplitType}
                    // helperText={
                    // <InfoIcon />
                    // }
                    register={register}
                    errors={errors}
                  />
                </div>
                <InfoIcon
                  message={
                    <>
                      Determines if amounts or percentages are used to split the
                      payout
                      <br />
                      <hr />
                      <span className="text-warning">NOTE:</span>
                      <br />
                      If % is used. All entries must total
                      <span className="text-warning">&nbsp;100%</span>
                      <br />
                      <br />
                      If amount is used. All entries must be less than or equal
                      to
                      <span className="text-warning">
                        &nbsp;line items total amount
                      </span>
                      <br />
                      If it is less than total, the balance will be paid out to
                      the &nbsp;
                      <span className="text-warning">
                        PRIMARY ACCOUNT NUMBER or CURRENCY WALLET
                      </span>
                    </>
                  }
                />
                {/* <HookFormCheckbox
                  name={`uses_payout_tag_percentage`}
                  label="Use percentages for splitting"
                  infoIconText={
                    <>
                      Determines if amounts or percentages are used to split the
                      payout
                      <br />
                      <hr />
                      <span className="text-warning">NOTE:</span>
                      <br />
                      If % is used. All entries must total
                      <span className="text-warning">&nbsp;100%</span>
                      <br />
                      <br />
                      If amount is used. All entries must be less than or equal
                      to
                      <span className="text-warning">
                        &nbsp;line items total amount
                      </span>
                      <br />
                      If it is less than total, the balance will be paid out to
                      the &nbsp;
                      <span className="text-warning">
                        PRIMARY ACCOUNT NUMBER or CURRENCY WALLET
                      </span>
                    </>
                  }
                  register={register}
                  errors={errors}
                /> */}

                {/* <HookFormToggle
                  name="uses_payout_tag_percentage_xx"
                  label="Use percentages for splitting"
                  // labelOff="No"
                  labelOff={<>No&nbsp;</>}
                  labelOn="Yes"
                  size="sm"
                  containerClassName="settings-toggle"
                  defaultToggled={formValues.uses_payout_tag_percentage}
                  // toggled={formValues.uses_payout_tag_percentage}
                  onToggle={onToggleSetSplitType}
                  infoIconText="Determines if amounts or percentages are used to split the payout"
                  infoIconAlignment="right"
                  register={register}
                  errors={errors}
                /> */}
              </div>
            )}

            {/* Render Payout Tags In List */}
            <div className="line-items-list--container">
              {payoutTagsFields.map((field, index) => {
                const poTagInField = formValues.transaction_payout_tags[index];

                const bankAccountOwningTag = bankAccountsInTagList.find((bnk) =>
                  bnk.bank_account_tags.find(
                    (tg) => tg.id === poTagInField.payout_tag
                  )
                );

                const bankTag = bankAccountOwningTag?.bank_account_tags.find(
                  (tg) => tg.id === poTagInField.payout_tag
                );

                return (
                  <div
                    key={field.id} // important to include key with field's id
                    className="line-item--container"
                  >
                    {
                      // field.product !== "" ? (
                      <div className="close-button--container">
                        <SwitchAppIconButton
                          size="sm"
                          kind="tertiary"
                          className="line-item--remove-button"
                          onClick={() => removePayoutTagField(index)}
                        >
                          <img
                            src={closeIconDark}
                            alt="close icon"
                            className="close-icon"
                          />
                        </SwitchAppIconButton>
                      </div>
                      // ) : null
                    }

                    {!bankAccountOwningTag ? (
                      <></>
                    ) : (
                      <>
                        <div className="flex-box flex-1_2">
                          {!bankAccountOwningTag || !bankTag ? null : (
                            <PayoutBankAccountDisplay
                              bankAccount={bankAccountOwningTag}
                              bankTag={bankTag}
                              payoutTag={poTagInField}
                            />
                          )}
                        </div>

                        <div className="flex-container flex-break--small">
                          <div className="flex-box">
                            {
                              <TextInput
                                name={`transaction_payout_tags.${index}.percentage`}
                                label={"Percentage"}
                                title={
                                  formValues.uses_payout_tag_percentage
                                    ? "Split Percentage"
                                    : "Disabled: Switch to percentages to enter value"
                                }
                                infoIconText={
                                  <>
                                    % of the total to pay out to this bank
                                    account.
                                    <br />
                                    <hr />
                                    <span className="text-warning">
                                      NOTE:
                                    </span>{" "}
                                    the app fee will be deducted before this
                                    splitting occurs if bearer is set to
                                    <span className="text-primary">
                                      &nbsp;merchant
                                    </span>
                                  </>
                                }
                                type="number"
                                size="sm"
                                className="mr-3"
                                disabled={
                                  !formValues.uses_payout_tag_percentage
                                }
                                register={register}
                                errors={errors}
                              />
                            }
                          </div>
                          <div className="flex-box">
                            {
                              <TextInput
                                name={`transaction_payout_tags.${index}.amount`}
                                label="Amount"
                                title={
                                  !formValues.uses_payout_tag_percentage
                                    ? "Split Amount"
                                    : "Disabled: Switch to amounts to enter value"
                                }
                                infoIconText={
                                  <>
                                    Fixed amount of the total to pay out to this
                                    bank account.
                                    <br />
                                    <hr />
                                    <span className="text-warning">
                                      NOTE:
                                    </span>{" "}
                                    the app fee will be deducted before this
                                    splitting occurs if bearer is set to
                                    <span className="text-primary">
                                      &nbsp;merchant
                                    </span>
                                  </>
                                }
                                type="number"
                                size="sm"
                                className="mr-3"
                                disabled={formValues.uses_payout_tag_percentage}
                                register={register}
                                errors={errors}
                              />
                            }
                          </div>
                          <div className="flex-box">
                            <TextInput
                              name={`transaction_payout_tags.${index}.app_fee_percentage`}
                              label="Fee % (optional)"
                              infoIconText={`% of the app fee to charge to the amount due for this payout tag.`}
                              type="number"
                              size="sm"
                              className="mr-3"
                              register={register}
                              errors={errors}
                            />
                          </div>
                          <div className="flex-box">
                            <TextInput
                              name={`transaction_payout_tags.${index}.description`}
                              label="Description"
                              infoIconText={`A description to accompany payouts for the payment link on each transaction. (optional)`}
                              type="text"
                              size="sm"
                              className="mr-3"
                              register={register}
                              errors={errors}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {!bankAccountsNotInTagList.length ? null : (
              <HookFormSelect
                name="payout_tag"
                label="" // "Preferred Payout Account(s)"
                containerClassName="no-top-margin"
                className="payout-tag--select"
                // infoIconText="Bank account where sales of this product will be paid out to. Leave empty if you want it to be paid into your primary acct. number."
                showSkeleton={isLoadingBankAccounts}
                selectItems={[]}
                selectGroupedItems={[
                  {
                    groupLabel: "Select A Tag To Add New Payout Tag",
                    options: [
                      {
                        text: "Select Preferred Payout Accounts From The List To Add Tags",
                        value: "",
                        disabled: true,
                      },
                    ],
                  },
                  ...bankAccountsNotInTagListSelectOptions,
                ]}
                onChange={(e) =>
                  appendPayoutTagField({
                    payout_tag: e.target.value,
                    amount: null,
                    percentage: null,
                    app_fee_percentage: null,
                    description: null,
                    metadata: null,
                  })
                }
                register={register}
                errors={errors}
              />
            )}

            <SectionSubheader
              title={
                <>
                  Social Media Links (
                  <label>
                    Show options
                    <input
                      type="checkbox"
                      {...register("enable_social_media_links")}
                    />
                  </label>
                  )
                </>
              }
              // infoIconDescription="Display social media links on customer checkout page"
            />

            {/* <HookFormCheckbox
              name="enable_social_media_links"
              label="Display social media links on checkout"
              infoIconText="Customers will see links to your social media pages for your products"
              register={register}
              errors={errors}
            /> */}

            {!formValues.enable_social_media_links ? null : (
              <div className="options--container">
                <TextInput
                  name={"instagram_url"}
                  label="Instagram Url"
                  infoIconText="An Instagram link to your product will be displayed on the checkout page for your customers."
                  type="text"
                  register={register}
                  errors={errors}
                />

                <TextInput
                  name={"facebook_url"}
                  label="Facebook Url"
                  infoIconText="An Facebook link to your product will be displayed on the checkout page for your customers."
                  type="text"
                  register={register}
                  errors={errors}
                />

                <TextInput
                  name={"whatsapp_url"}
                  label="WhatsApp Url"
                  infoIconText="A WhatsApp link to your product will be displayed on the checkout page for your customers."
                  type="text"
                  register={register}
                  errors={errors}
                />
              </div>
            )}

            <SectionSubheader
              title={
                <>
                  For Developers (
                  <label>
                    Show options
                    <input
                      type="checkbox"
                      {...register("enable_developer_options")}
                    />
                  </label>
                  )
                </>
              }
              // infoIconDescription="configure a custom webhook for this payment link"
            />
            {/* 
            <HookFormCheckbox
              name="enable_developer_options"
              label="Set custom webhook to call for payment link sales"
              infoIconText=""
              register={register}
              errors={errors}
            /> */}

            {!formValues.enable_developer_options ? null : (
              <>
                <TextInput
                  name={"live_webhook_url"}
                  label="Live Webhook Url"
                  infoIconText="A url endpoint that will be called whenever someone makes a 
                  purchase using this payment link in LIVE mode. The webhook for payments will still be called 
                  so make sure the calls are idempotent."
                  type="text"
                  register={register}
                  errors={errors}
                />
                <TextInput
                  name={"test_webhook_url"}
                  label="Test Webhook Url"
                  infoIconText="A url endpoint that will be called whenever someone makes a 
                  purchase using this payment link in TEST mode. The webhook for payments will still be called 
                  so make sure the calls are idempotent."
                  type="text"
                  register={register}
                  errors={errors}
                />
              </>
            )}
          </div>

          {/* <div className="flex-divider--vertical" /> */}
          <div className="preview-section">
            <div className="preview--container flex-container col">
              <SectionSubheader
                title="Preview"
                infoIconDescription="This is what your customers see at checkout. You can configure more options in branding settings."
              />

              <PaymentLinkPreview
                paymentLinkToPreview={formValues}
                productsInPaymentLink={productsInPaymentLink}
              />
            </div>
          </div>
        </div>
      </ModalContainer>
    </form>
  );
};
